import { selectAccount } from 'common-js/reducers/account/selectors';
import { autoJoinOrg, changeContext } from 'common-js/reducers/organization/actions';
import { selectOrganization } from 'common-js/reducers/organization/selectors';
import { useEffect, useRef } from 'react';
import { type RouteComponentProps } from 'react-router';
import useAppDispatch from './useAppDispatch';
import useAppSelector from './useAppSelector';

interface UseAutoAcceptOrgInviteProps extends RouteComponentProps<{}, RouterQueryParams> {}

const useAutoAcceptOrgInvite = ({ location }: UseAutoAcceptOrgInviteProps) => {
  const dispatch = useAppDispatch();
  const { userId } = useAppSelector(selectAccount) ?? {};

  const { orgs } = useAppSelector(selectOrganization) ?? {};
  const { orgid, inviteid, token, origin } = location.query;

  const joiningOrgRef = useRef(false);

  useEffect(() => {
    const joinOrg = async () => {
      if (joiningOrgRef.current) {
        return;
      }
      joiningOrgRef.current = true;

      const inviteOrgId = parseInt(orgid as string, 10);
      const hasInviteOrg = orgs?.some((org) => org.id === inviteOrgId);

      if (hasInviteOrg) {
        // User already has the org, but now change context to it if you followed the link. No modal for this case.
        await dispatch(changeContext(inviteOrgId, true, null, true));
        joiningOrgRef.current = false;
        return;
      }

      if (!hasInviteOrg) {
        try {
          await dispatch(autoJoinOrg(orgid!, inviteid!, token));
        } catch (error) {
          // Ignore errors
          // eslint-disable-next-line no-console
          console.error(error);
        }
        await dispatch(changeContext(inviteOrgId, true, null, true));
      }
      joiningOrgRef.current = false;
    };

    if (origin === 'orginviteemail' && userId) {
      joinOrg();
    }
  }, [dispatch, inviteid, orgid, orgs, origin, token, userId]);
};

export default useAutoAcceptOrgInvite;
